import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Lock,
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Calendar as CalIcon,
  CalendarDays,
  ClipboardList,
  MessageSquare,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Trash2,
  Plus,
  Code2,
  Save,
  Layers,
  Globe,
  AlertTriangle,
} from "lucide-react";

import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN_KEY = "bfl_admin_pin";

const inputClass =
  "w-full bg-slate-50 border border-slate-300 rounded-none focus-visible:border-[#1F3460] focus-visible:ring-1 focus-visible:ring-[#1F3460] focus-visible:ring-offset-0 h-12 px-4 text-[#1F3460] placeholder:text-slate-400";

const SUPPORTED_CITIES = [
  "Naples", "Bonita Springs", "Estero", "Marco Island",
  "Fort Myers", "Cape Coral", "Lehigh Acres", "North Fort Myers",
  "San Carlos Park", "Punta Gorda", "Port Charlotte", "North Port",
  "Sarasota", "Immokalee",
];

const DURATION_OPTIONS = [
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
  { value: 180, label: "3 hours" },
  { value: 240, label: "4 hours" },
  { value: 360, label: "6 hours" },
  { value: 540, label: "All day (9h)" },
];

const statusOptions = ["pending", "confirmed", "completed", "cancelled"];

const statusBadge = (s) => {
  const map = {
    pending: { bg: "bg-[#6FA8DC]", text: "text-[#1F3460]", icon: Clock },
    confirmed: { bg: "bg-[#1F3460]", text: "text-white", icon: CheckCircle2 },
    completed: { bg: "bg-emerald-600", text: "text-white", icon: CheckCircle2 },
    cancelled: { bg: "bg-slate-400", text: "text-white", icon: XCircle },
  };
  const m = map[s] || map.pending;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-black tracking-widest uppercase ${m.bg} ${m.text}`}
    >
      <Icon className="w-3 h-3" /> {s}
    </span>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const fmt12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
};

const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 6; h <= 20; h++) {
    for (let m of [0, 15, 30, 45]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
})();

export default function Admin() {
  const [pin, setPin] = useState(localStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [tab, setTab] = useState("unified");

  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [embedHtml, setEmbedHtml] = useState("");
  const [embedDraft, setEmbedDraft] = useState("");
  const [embedSaving, setEmbedSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Unified view
  const [unified, setUnified] = useState({ items: [], counts: { site: 0, ghl: 0, total: 0 }, ghl_configured: false, ghl_error: null });
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all"); // all | site | ghl

  // Block creation form
  const [blkDate, setBlkDate] = useState(null);
  const [blkDateOpen, setBlkDateOpen] = useState(false);
  const [blkTime, setBlkTime] = useState("");
  const [blkDuration, setBlkDuration] = useState(60);
  const [blkCity, setBlkCity] = useState("Naples");
  const [blkLabel, setBlkLabel] = useState("");
  const [creatingBlock, setCreatingBlock] = useState(false);

  const headers = useCallback(() => ({ "X-Admin-Pin": pin }), [pin]);

  const verify = async (currentPin) => {
    setVerifying(true);
    try {
      await axios.post(
        `${API}/admin/verify`,
        {},
        { headers: { "X-Admin-Pin": currentPin } }
      );
      localStorage.setItem(PIN_KEY, currentPin);
      setAuthed(true);
    } catch {
      setAuthed(false);
      localStorage.removeItem(PIN_KEY);
      toast.error("Invalid admin pin");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (pin) verify(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const [a, c, b, e] = await Promise.all([
        axios.get(`${API}/appointments`, { headers: headers() }),
        axios.get(`${API}/contact`, { headers: headers() }),
        axios.get(`${API}/blocks`, { headers: headers() }),
        axios.get(`${API}/admin/embed`, { headers: headers() }),
      ]);
      setAppointments(a.data || []);
      setContacts(c.data || []);
      setBlocks(b.data || []);
      const html = e.data?.embed_html || "";
      setEmbedHtml(html);
      setEmbedDraft(html);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem(PIN_KEY);
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [authed, headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadUnified = useCallback(async () => {
    if (!authed) return;
    setUnifiedLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/appointments/unified?days_ahead=60`, {
        headers: headers(),
      });
      setUnified(data || { items: [], counts: { site: 0, ghl: 0, total: 0 } });
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Could not load unified list");
    } finally {
      setUnifiedLoading(false);
    }
  }, [authed, headers]);

  useEffect(() => {
    if (tab === "unified") loadUnified();
  }, [tab, loadUnified]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/appointments/${id}`,
        { status },
        { headers: headers() }
      );
      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status } : a))
      );
      toast.success(
        status === "cancelled"
          ? "Cancelled — customer notified by email."
          : `Status updated to ${status}`
      );
    } catch {
      toast.error("Could not update status");
    }
  };

  const createBlock = async () => {
    if (!blkDate || !blkTime || !blkCity || !blkDuration) {
      toast.error("Please fill date, time, duration and city.");
      return;
    }
    setCreatingBlock(true);
    try {
      const payload = {
        label: blkLabel || "",
        date: format(blkDate, "yyyy-MM-dd"),
        time: blkTime,
        duration_minutes: Number(blkDuration),
        city: blkCity,
      };
      const { data } = await axios.post(`${API}/blocks`, payload, {
        headers: headers(),
      });
      setBlocks((prev) => [...prev, data].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)));
      setBlkDate(null);
      setBlkTime("");
      setBlkLabel("");
      toast.success("Schedule block added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create block");
    } finally {
      setCreatingBlock(false);
    }
  };

  const deleteBlock = async (id) => {
    try {
      await axios.delete(`${API}/blocks/${id}`, { headers: headers() });
      setBlocks((prev) => prev.filter((b) => b.id !== id));
      toast.success("Block removed.");
    } catch {
      toast.error("Could not delete block");
    }
  };

  const saveEmbed = async () => {
    setEmbedSaving(true);
    try {
      await axios.put(
        `${API}/admin/embed`,
        { embed_html: embedDraft },
        { headers: headers() }
      );
      setEmbedHtml(embedDraft);
      toast.success("Calendar embed saved.");
    } catch {
      toast.error("Could not save embed.");
    } finally {
      setEmbedSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(PIN_KEY);
    setPin("");
    setAuthed(false);
    setAppointments([]);
    setContacts([]);
    setBlocks([]);
    setEmbedHtml("");
    setEmbedDraft("");
  };

  const groupedBlocks = useMemo(() => {
    const m = {};
    for (const b of blocks) {
      m[b.date] = m[b.date] || [];
      m[b.date].push(b);
    }
    return Object.entries(m).sort(([d1], [d2]) => d1.localeCompare(d2));
  }, [blocks]);

  // ===== LOGIN GATE =====
  if (!authed) {
    return (
      <div
        data-testid="admin-login"
        className="min-h-[calc(100vh-72px)] bg-slate-50 flex items-center justify-center px-5"
      >
        <div className="w-full max-w-md bg-white border border-[#E2E8F0] p-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#1F3460] flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#6FA8DC]" />
            </div>
            <div>
              <div className="overline">Restricted</div>
              <h1 className="font-display text-2xl font-black text-[#1F3460]">
                Admin Dashboard
              </h1>
            </div>
          </div>
          <p className="mt-5 text-sm text-[#5E6B85]">
            Enter your admin pin to view appointment and contact submissions.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (pin) verify(pin);
            }}
            className="mt-6 space-y-4"
          >
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">
                Admin Pin
              </Label>
              <Input
                type="password"
                data-testid="admin-pin-input"
                className={inputClass}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="••••••••"
                autoFocus
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={verifying || !pin}
              className="btn-primary w-full disabled:opacity-60"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                </>
              ) : (
                "Enter Dashboard"
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ===== DASHBOARD =====
  return (
    <div data-testid="admin-dashboard" className="min-h-[calc(100vh-72px)] bg-slate-50">
      <div className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="overline">Internal</div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-[#1F3460]">
              Admin Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadData}
              data-testid="admin-refresh"
              className="btn-outline"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="btn-primary">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-8 flex gap-1 overflow-x-auto">
          {[
            { id: "unified", label: "Unified", count: null, Icon: Layers },
            { id: "appointments", label: "Site Requests", count: appointments.length, Icon: ClipboardList },
            { id: "contacts", label: "Messages", count: contacts.length, Icon: MessageSquare },
            { id: "schedule", label: "Schedule Blocks", count: blocks.length, Icon: CalendarDays },
            { id: "calendar", label: "GHL Calendar", count: null, Icon: Code2 },
          ].map(({ id, label, count, Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-4 transition-colors whitespace-nowrap ${
                tab === id
                  ? "border-[#6FA8DC] text-[#1F3460]"
                  : "border-transparent text-[#5E6B85] hover:text-[#1F3460]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {count !== null && (
                <span className="text-xs bg-slate-100 px-2 py-0.5">{count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {tab === "unified" && (
          <div data-testid="admin-unified" className="space-y-6">
            {/* Header / source filter */}
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="overline flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5" /> All Sources · Next 60 days
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-black text-[#1F3460] mt-1">
                  Unified appointment list
                </h2>
                <p className="mt-2 text-sm text-[#5E6B85] max-w-2xl">
                  Site bookings + GoHighLevel calendar appointments, merged and sorted by date.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="unified-refresh"
                  onClick={loadUnified}
                  className="btn-outline"
                  disabled={unifiedLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${unifiedLoading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Source pills */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { id: "all", label: `All · ${unified.counts?.total ?? 0}` },
                { id: "site", label: `Site · ${unified.counts?.site ?? 0}` },
                { id: "ghl", label: `GHL · ${unified.counts?.ghl ?? 0}` },
              ].map((p) => (
                <button
                  key={p.id}
                  data-testid={`source-filter-${p.id}`}
                  onClick={() => setSourceFilter(p.id)}
                  className={`px-3.5 py-1.5 text-xs font-bold tracking-wider uppercase border-2 transition-all ${
                    sourceFilter === p.id
                      ? "bg-[#1F3460] text-white border-[#1F3460]"
                      : "bg-white text-[#5E6B85] border-slate-300 hover:border-[#1F3460]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {unified.ghl_configured && (
                <span className="ml-2 text-[10px] tracking-widest uppercase text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> GHL connected
                </span>
              )}
            </div>

            {unified.ghl_error && (
              <div
                data-testid="ghl-error-banner"
                className="p-4 bg-amber-50 border-l-4 border-amber-400 text-sm text-amber-900 flex items-start gap-2"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>GHL couldn't load:</strong> {unified.ghl_error}
                  <div className="text-xs mt-1 text-amber-800">
                    Showing site bookings only. Check your Private Integration token and scopes (calendars.readonly, calendars/events.readonly, contacts.readonly).
                  </div>
                </div>
              </div>
            )}

            {!unified.ghl_error && unified.ghl_configured && unified.counts?.ghl === 0 && !unifiedLoading && (
              <div
                data-testid="ghl-hint-banner"
                className="p-4 bg-[#6FA8DC]/10 border-l-4 border-[#6FA8DC] text-sm text-[#1F3460] flex items-start gap-2"
              >
                <Globe className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#1F3460]" />
                <div>
                  <strong>GHL is connected but no calendars are syncing yet.</strong>
                  <div className="text-xs mt-1 text-[#5E6B85]">
                    Once you create a calendar in your GoHighLevel sub-account (Settings → Calendars), bookings will show up here automatically — no code changes needed.
                  </div>
                </div>
              </div>
            )}

            {/* List */}
            {unifiedLoading ? (
              <div className="border border-[#E2E8F0] bg-white p-10 text-center text-[#5E6B85]">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </div>
            ) : (() => {
                const filtered = (unified.items || []).filter(
                  (it) => sourceFilter === "all" || it.source === sourceFilter
                );
                if (filtered.length === 0) {
                  return (
                    <div data-testid="unified-empty" className="border border-[#E2E8F0] bg-white p-10 text-center text-[#5E6B85]">
                      {unified.counts?.total === 0
                        ? "No upcoming appointments in the next 60 days."
                        : "No appointments match this filter."}
                    </div>
                  );
                }
                // Group by date
                const groups = {};
                for (const it of filtered) {
                  const key = (it.start_iso || "").slice(0, 10) || "—";
                  groups[key] = groups[key] || [];
                  groups[key].push(it);
                }
                const entries = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
                return (
                  <div data-testid="unified-list" className="space-y-4">
                    {entries.map(([dateKey, items]) => (
                      <div key={dateKey} className="bg-white border border-[#E2E8F0]">
                        <div className="px-5 py-3 bg-slate-50 border-b border-[#E2E8F0]">
                          <div className="overline">Date</div>
                          <div className="font-bold text-[#1F3460]">
                            {(() => {
                              try { return format(new Date(dateKey + "T00:00:00"), "EEEE, MMMM d, yyyy"); }
                              catch { return dateKey; }
                            })()}
                          </div>
                        </div>
                        <div className="divide-y divide-[#E2E8F0]">
                          {items.map((it) => {
                            const t = it.start_iso ? it.start_iso.slice(11, 16) : "";
                            const isGhl = it.source === "ghl";
                            return (
                              <div
                                key={`${it.source}-${it.id}`}
                                data-testid={`unified-row-${it.source}-${it.id}`}
                                className="px-5 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start"
                              >
                                <div className="md:col-span-2 flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-[#6FA8DC]" />
                                  <span className="font-bold text-[#1F3460]">
                                    {t ? fmt12(t) : "—"}
                                  </span>
                                </div>
                                <div className="md:col-span-3">
                                  <div className="font-bold text-[#1F3460]">{it.contact_name || "—"}</div>
                                  {it.contact_email && (
                                    <a href={`mailto:${it.contact_email}`} className="text-xs text-[#5E6B85] hover:text-[#1F3460] break-all">
                                      {it.contact_email}
                                    </a>
                                  )}
                                  {it.contact_phone && (
                                    <a href={`tel:${it.contact_phone}`} className="block text-xs text-[#5E6B85] hover:text-[#1F3460]">
                                      {it.contact_phone}
                                    </a>
                                  )}
                                </div>
                                <div className="md:col-span-3 text-sm text-[#1F3460]">
                                  <div className="overline text-[10px]">Service</div>
                                  {it.service || "—"}
                                  {it.address && (
                                    <div className="mt-1 text-xs text-[#5E6B85] flex items-start gap-1">
                                      <MapPin className="w-3 h-3 mt-0.5 text-[#6FA8DC]" /> {it.address}
                                    </div>
                                  )}
                                </div>
                                <div className="md:col-span-2 text-sm text-[#1F3460]">
                                  <div className="overline text-[10px]">Calendar</div>
                                  {it.calendar_name || "—"}
                                </div>
                                <div className="md:col-span-2 flex flex-col items-start md:items-end gap-1.5">
                                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-black tracking-widest uppercase ${
                                    isGhl
                                      ? "bg-[#C9A86B] text-white"
                                      : "bg-[#6FA8DC] text-[#1F3460]"
                                  }`}>
                                    {isGhl ? <Globe className="w-3 h-3" /> : <ClipboardList className="w-3 h-3" />}
                                    {isGhl ? "GHL" : "Site"}
                                  </span>
                                  <span className="text-[10px] tracking-widest uppercase text-[#5E6B85]">
                                    {it.status || "—"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
          </div>
        )}

        {tab === "appointments" && (
          <div data-testid="admin-appointments-list" className="space-y-4">
            {appointments.length === 0 && (
              <div className="border border-[#E2E8F0] bg-white p-10 text-center text-[#5E6B85]">
                No appointment requests yet.
              </div>
            )}
            {appointments.map((a) => (
              <div
                key={a.id}
                data-testid={`appt-card-${a.id}`}
                className="bg-white border border-[#E2E8F0] p-6 md:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-xl md:text-2xl font-black text-[#1F3460]">
                        {a.name}
                      </h3>
                      {statusBadge(a.status)}
                    </div>
                    <div className="overline mt-1">{a.service_type}{a.purpose ? ` · ${a.purpose}` : ""}</div>
                  </div>
                  <div className="text-right text-xs text-[#5E6B85]">
                    <div>Submitted: {fmtDate(a.created_at)}</div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <CalIcon className="w-4 h-4 mt-0.5 text-[#6FA8DC]" />
                    <div>
                      <div className="overline text-[10px]">When</div>
                      <div className="font-bold text-[#1F3460]">
                        {a.preferred_date}
                        {a.preferred_time ? ` · ${fmt12(a.preferred_time)}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 mt-0.5 text-[#6FA8DC]" />
                    <a href={`mailto:${a.email}`} className="text-[#1F3460] hover:underline break-all">
                      {a.email}
                    </a>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 mt-0.5 text-[#6FA8DC]" />
                    <a href={`tel:${a.phone}`} className="text-[#1F3460] hover:underline">
                      {a.phone}
                    </a>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-[#6FA8DC]" />
                    <span className="text-[#1F3460]">
                      {a.address}
                      {a.city ? ` · ${a.city}` : ""}
                    </span>
                  </div>
                </div>

                {a.notes && (
                  <div className="mt-4 p-4 bg-slate-50 border-l-4 border-[#6FA8DC] text-sm text-[#1F3460] whitespace-pre-wrap">
                    {a.notes}
                  </div>
                )}

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#5E6B85]">
                    Status
                  </Label>
                  <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                    <SelectTrigger
                      data-testid={`appt-status-${a.id}`}
                      className="h-10 w-44 rounded-none bg-slate-50 border border-slate-300"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {statusOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-[#5E6B85]">
                    Email delivered: {a.email_sent ? "yes" : "no"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "contacts" && (
          <div data-testid="admin-contacts-list" className="space-y-4">
            {contacts.length === 0 && (
              <div className="border border-[#E2E8F0] bg-white p-10 text-center text-[#5E6B85]">
                No contact messages yet.
              </div>
            )}
            {contacts.map((c) => (
              <div
                key={c.id}
                data-testid={`contact-card-${c.id}`}
                className="bg-white border border-[#E2E8F0] p-6 md:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-black text-[#1F3460]">
                      {c.name}
                    </h3>
                    <div className="mt-1 flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-[#5E6B85]">
                      <a href={`mailto:${c.email}`} className="hover:text-[#1F3460] break-all">
                        <Mail className="inline w-3.5 h-3.5 mr-1 text-[#6FA8DC]" />
                        {c.email}
                      </a>
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="hover:text-[#1F3460]">
                          <Phone className="inline w-3.5 h-3.5 mr-1 text-[#6FA8DC]" />
                          {c.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-xs text-[#5E6B85]">{fmtDate(c.created_at)}</div>
                </div>

                <div className="mt-5 p-4 bg-slate-50 border-l-4 border-[#6FA8DC] text-[#1F3460] whitespace-pre-wrap">
                  {c.message}
                </div>
                <div className="mt-3 text-xs text-[#5E6B85]">
                  Email delivered: {c.email_sent ? "yes" : "no"}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "schedule" && (
          <div data-testid="admin-schedule" className="space-y-8">
            {/* Create form */}
            <div className="bg-white border border-[#E2E8F0] p-6 md:p-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1F3460] flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-[#6FA8DC]" />
                </div>
                <div>
                  <div className="overline">Block personal time</div>
                  <h2 className="font-display text-xl md:text-2xl font-black text-[#1F3460]">
                    Add a schedule block
                  </h2>
                </div>
              </div>
              <p className="mt-4 text-sm text-[#5E6B85] max-w-2xl">
                Block time off for errands, family, or anything else. The scheduler will reserve this time AND the
                drive back to Naples (home base) afterward, so customers can't book a slot you can't physically reach.
              </p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Date */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">Date *</Label>
                  <Popover open={blkDateOpen} onOpenChange={setBlkDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="block-date-trigger"
                        className={`${inputClass} flex items-center justify-between text-left`}
                      >
                        <span className={blkDate ? "text-[#1F3460]" : "text-slate-400"}>
                          {blkDate ? format(blkDate, "PPP") : "Pick a date"}
                        </span>
                        <CalIcon className="w-4 h-4 text-[#1F3460]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-none border-2 border-[#1F3460] bg-white" align="start">
                      <Calendar
                        mode="single"
                        selected={blkDate}
                        onSelect={(d) => {
                          setBlkDate(d);
                          setBlkDateOpen(false);
                        }}
                        disabled={(d) => {
                          const today = new Date(new Date().setHours(0, 0, 0, 0));
                          return d < today;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">Start Time *</Label>
                  <Select value={blkTime} onValueChange={setBlkTime}>
                    <SelectTrigger
                      data-testid="block-time-trigger"
                      className="h-12 rounded-none bg-slate-50 border border-slate-300"
                    >
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none max-h-72">
                      {TIME_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`block-time-${t}`}>
                          {fmt12(t)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Duration */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">Duration *</Label>
                  <Select value={String(blkDuration)} onValueChange={(v) => setBlkDuration(Number(v))}>
                    <SelectTrigger
                      data-testid="block-duration-trigger"
                      className="h-12 rounded-none bg-slate-50 border border-slate-300"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {DURATION_OPTIONS.map((d) => (
                        <SelectItem key={d.value} value={String(d.value)}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">Location (city) *</Label>
                  <Select value={blkCity} onValueChange={setBlkCity}>
                    <SelectTrigger
                      data-testid="block-city-trigger"
                      className="h-12 rounded-none bg-slate-50 border border-slate-300"
                    >
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none max-h-72">
                      {SUPPORTED_CITIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Label */}
                <div className="md:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#1F3460]">Label (optional)</Label>
                  <Input
                    data-testid="block-label"
                    className={inputClass}
                    placeholder="e.g. Driving Marissa to school"
                    value={blkLabel}
                    onChange={(e) => setBlkLabel(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={createBlock}
                disabled={creatingBlock}
                data-testid="block-create-submit"
                className="btn-accent mt-6 disabled:opacity-60"
              >
                {creatingBlock ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</>
                ) : (
                  <><Plus className="w-4 h-4" /> Add Block</>
                )}
              </button>
            </div>

            {/* Existing blocks */}
            <div data-testid="admin-blocks-list" className="space-y-4">
              <h2 className="font-display text-xl md:text-2xl font-black text-[#1F3460]">
                Current blocks
              </h2>
              {blocks.length === 0 && (
                <div className="border border-[#E2E8F0] bg-white p-10 text-center text-[#5E6B85]">
                  No personal time blocks. Add one above.
                </div>
              )}
              {groupedBlocks.map(([dateKey, items]) => (
                <div key={dateKey} className="bg-white border border-[#E2E8F0]">
                  <div className="px-5 py-3 bg-slate-50 border-b border-[#E2E8F0]">
                    <div className="overline">Date</div>
                    <div className="font-bold text-[#1F3460]">
                      {(() => {
                        try { return format(new Date(dateKey + "T00:00:00"), "EEEE, MMMM d, yyyy"); }
                        catch { return dateKey; }
                      })()}
                    </div>
                  </div>
                  <div className="divide-y divide-[#E2E8F0]">
                    {items.map((b) => (
                      <div
                        key={b.id}
                        data-testid={`block-row-${b.id}`}
                        className="px-5 py-4 flex flex-wrap items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-5 flex-wrap text-sm">
                          <div className="flex items-center gap-2 text-[#1F3460] font-bold">
                            <Clock className="w-4 h-4 text-[#6FA8DC]" />
                            {fmt12(b.time)} · {b.duration_minutes} min
                          </div>
                          <div className="flex items-center gap-2 text-[#1F3460]">
                            <MapPin className="w-4 h-4 text-[#6FA8DC]" />
                            {b.city}
                          </div>
                          {b.label && (
                            <div className="text-[#5E6B85] italic">"{b.label}"</div>
                          )}
                        </div>
                        <button
                          type="button"
                          data-testid={`block-delete-${b.id}`}
                          onClick={() => deleteBlock(b.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 border-red-400 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "calendar" && (
          <div data-testid="admin-calendar" className="space-y-8">
            <div className="bg-white border border-[#E2E8F0] p-6 md:p-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1F3460] flex items-center justify-center">
                  <Code2 className="w-5 h-5 text-[#6FA8DC]" />
                </div>
                <div>
                  <div className="overline">GHL Calendar embed</div>
                  <h2 className="font-display text-xl md:text-2xl font-black text-[#1F3460]">
                    Paste your GoHighLevel calendar code
                  </h2>
                </div>
              </div>
              <p className="mt-4 text-sm text-[#5E6B85] max-w-2xl">
                Paste any HTML — iframe, &lt;script&gt;, or full embed snippet — from your GHL calendar (or any other
                source). It will render below so you can see both scheduling sources in one place. Only you see this
                tab; the public site is unaffected.
              </p>

              <Label className="mt-6 block text-xs font-bold uppercase tracking-widest text-[#1F3460]">
                Embed HTML
              </Label>
              <Textarea
                data-testid="embed-html-input"
                rows={10}
                value={embedDraft}
                onChange={(e) => setEmbedDraft(e.target.value)}
                placeholder='<iframe src="https://api.leadconnectorhq.com/widget/booking/..." style="width:100%; height:800px; border:none;"></iframe>'
                className="w-full bg-slate-50 border border-slate-300 rounded-none focus-visible:border-[#1F3460] focus-visible:ring-1 focus-visible:ring-[#1F3460] focus-visible:ring-offset-0 px-4 py-3 text-[#1F3460] placeholder:text-slate-400 font-mono text-sm"
              />
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="embed-save"
                  onClick={saveEmbed}
                  disabled={embedSaving || embedDraft === embedHtml}
                  className="btn-primary disabled:opacity-60"
                >
                  {embedSaving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4" /> Save embed</>
                  )}
                </button>
                {embedDraft !== embedHtml && (
                  <button
                    type="button"
                    onClick={() => setEmbedDraft(embedHtml)}
                    className="text-xs uppercase tracking-wider text-[#5E6B85] hover:text-[#1F3460]"
                  >
                    Discard changes
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#E2E8F0]">
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
                <div>
                  <div className="overline">Live preview</div>
                  <h3 className="font-display text-lg font-black text-[#1F3460]">
                    Your scheduling calendar
                  </h3>
                </div>
              </div>
              <div className="p-2 md:p-6 min-h-[400px]">
                {embedHtml ? (
                  <div
                    data-testid="embed-preview"
                    dangerouslySetInnerHTML={{ __html: embedHtml }}
                  />
                ) : (
                  <div data-testid="embed-empty" className="text-center text-[#5E6B85] py-16">
                    No calendar embed yet. Paste your GHL code above and click "Save embed".
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
